import { useEffect, useState } from 'react';
import { useSupabase } from '../../../lib/SupabaseProvider';
import { captureException } from '../../../lib/telemetry/posthog/frontend';
import type { StructuredReportData } from './InterviewReport';

/** Shared saved-report state, independent of which response tab is mounted. */
export interface SavedReportState { reportData: StructuredReportData | null; loading: boolean }

/** Fetch through the entitled view and never expose another response's annotations while loading. */
export function useSavedReport(interviewId: string, responseId?: string, enabled = true): SavedReportState {
  const { supabase } = useSupabase();
  const key = `${interviewId}:${responseId ?? ''}`;
  const [state, setState] = useState<SavedReportState & { key: string }>({ key: '', reportData: null, loading: true });
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    /** Read the persisted report once for both report cards and inline context. */
    async function load() {
      let reportData: StructuredReportData | null = null;
      try {
        let query = supabase.from('case_studies_visible').select('content').eq('interview_session_id', interviewId).eq('content_type', 'report');
        query = responseId ? query.eq('interview_data_id', responseId) : query.is('interview_data_id', null);
        const { data, error } = await query.maybeSingle();
        if (error) throw error;
        if (data?.content) {
          // Legacy HTML reports do not have structured annotations.
          try {
            const parsed: StructuredReportData = JSON.parse(data.content);
            if (typeof parsed.tldr === 'string' && Array.isArray(parsed.sentimentAnalysis)) reportData = parsed;
          } catch { /* Expected for historical HTML content. */ }
        }
      } catch (error) {
        captureException(error instanceof Error ? error : new Error('Saved report unavailable'), 'useSavedReport.load', { interviewId, responseId });
      }
      if (!cancelled) setState({ key, reportData, loading: false });
    }
    void load();
    return () => { cancelled = true; };
  }, [supabase, interviewId, responseId, key, enabled]);
  return enabled && state.key === key ? state : { reportData: null, loading: enabled };
}
